import React from 'react'
import styled from '@emotion/styled'
import { Grid, GridCell, GridRow } from '@mobi/component-library/Legacy/Grid'
import { Icon } from '@mobi/component-library/Legacy/Icon'
import { hexColors } from '@mobi/settings/hexColors'
import { colors } from '@core/Settings'
import { ButtonBlock } from '@core/Components/Buttons/ButtonBlock'
import type { NoticeBoxTypes } from './types'

type Props = {
  title?: string | null
  subtitle?: string | null
  noticeBoxType?: NoticeBoxTypes
  hideIcon?: boolean
  hasBorder?: boolean
  className?: string
  testId?: string | null
  buttonClick?: () => void
  buttonText?: string
  children?: React.ReactNode
  paddingOverride?: React.CSSProperties['padding']
} & Pick<React.HTMLAttributes<HTMLDivElement>, 'role' | 'aria-label'>

export const NoticeBox: React.FC<Props> = ({
  title,
  subtitle,
  noticeBoxType,
  hideIcon = false,
  hasBorder,
  children,
  className,
  testId = title,
  buttonClick,
  buttonText = 'OK',
  paddingOverride,
  ...htmlAttributes
}) => {
  const titleTestId = { 'data-tid-notice-box-title': title }

  return (
    <NoticeBoxStyled
      className={className}
      data-tid-notice-box={testId}
      data-testid={testId}
      {...htmlAttributes}
      style={{
        border: hasBorder ? `0.1rem solid ${hexColors.veryLightGrey}` : undefined,
        backgroundColor: hasBorder ? hexColors.white : undefined,
        padding: paddingOverride,
      }}
    >
      <Grid>
        {!hideIcon && (
          <NoticeBoxIconStyled>
            <NoticeBoxIcon type={noticeBoxType} />
          </NoticeBoxIconStyled>
        )}

        <GridCell valign='middle'>
          {title && (
            <NoticeBoxTitleStyled {...titleTestId}>
              <strong>{title}</strong>
            </NoticeBoxTitleStyled>
          )}

          {(subtitle || children) && (
            <NoticeBoxChildContentWrapperStyled data-tid-notice-box-children={title}>
              {subtitle ? <p>{subtitle}</p> : null}
              {children}
            </NoticeBoxChildContentWrapperStyled>
          )}
        </GridCell>

        {buttonClick && (
          <GridRow padding='2rem 0 0 0'>
            <GridCell width='100%' valign='middle'>
              <ButtonBlock onClick={buttonClick} color='primary'>
                {buttonText}
              </ButtonBlock>
            </GridCell>
          </GridRow>
        )}
      </Grid>
    </NoticeBoxStyled>
  )
}

export const NoticeBoxSingle: React.FC<{
  title: React.ReactNode
  noticeBoxType?: NoticeBoxTypes
  hasBorder?: boolean
  testId?: string
  isDesktop?: boolean
}> = ({ title, noticeBoxType, hasBorder, testId, isDesktop }) => {
  const testAutomationValue = testId || ''
  const testAutomationId = { 'data-tid-notice-box-single': testAutomationValue }

  return (
    <NoticeBoxStyled
      {...testAutomationId}
      style={{
        border: hasBorder ? `0.1rem solid ${hexColors.veryLightGrey}` : undefined,
        backgroundColor: hasBorder ? hexColors.white : undefined,
        fontSize: isDesktop ? '1.4rem' : '1.2rem',
        padding: '0.5rem',
      }}
    >
      <Grid>
        <NoticeBoxIconStyled>
          <NoticeBoxIcon type={noticeBoxType} />
        </NoticeBoxIconStyled>
        <GridCell valign='middle'>{title}</GridCell>
      </Grid>
    </NoticeBoxStyled>
  )
}

// ================
// Local Components
// ================

const NoticeBoxIcon = ({ type }: { type?: NoticeBoxTypes }) => {
  if (type === undefined)
    return <Icon type='infoCircled' size='3.2rem' color={colors.messages.info.text} />

  switch (type) {
    case 'Error':
      return <Icon type='errorCircled' size='3.2rem' color={colors.messages.error.text} />
    case 'Success':
      return <Icon type='tickCircled' size='3.2rem' color={colors.messages.success.text} />
    case 'Warning':
      return <Icon type='warningCircled' size='3.2rem' color={colors.messages.warning.text} />
    default:
    case 'Info':
      return <Icon type='infoCircled' size='3.2rem' color={colors.messages.info.text} />
  }
}

// ======
// Styles
// ======

const NoticeBoxStyled = styled.div({
  lineHeight: 1.2,
  margin: '0 0 1rem',
  padding: '1rem',
  fontSize: '1.2rem',
})

const NoticeBoxIconStyled = styled.div({
  display: 'flex',
  width: '4.2rem',
})

const NoticeBoxTitleStyled = styled.p({
  fontSize: '1.4rem', // overriding mobi styles
  margin: '0 0 1rem',

  // NoticeBoxTitle can be rendered by itself (no subtitle or children)
  '&:last-child': { marginBottom: 0 },
})

const NoticeBoxChildContentWrapperStyled = styled.div({
  fontSize: '1.2rem',

  // As we allow children to be passed into the <NoticeBox> component,
  // we need to override mobi styles at least for paragraphs. Any other kind of content
  // or component used as a child of NoticeBox will require its own styling.
  // Source of the problem: `scss/base/_legacy-globals.scss`
  '& p': {
    fontSize: 'inherit',
    textAlign: 'inherit',
    margin: '1rem 0',

    '&:last-child': { marginBottom: 0 },
  },
})
